'use client';
import { Divider } from '@mantine/core';

import { Button } from '@scaleup/frontend/ui';
import { toTitleCase } from '@scaleup/frontend/utils';
import { IconArrowRight, IconCheck, IconListCheck } from '@tabler/icons-react';
import classNames from 'classnames';
import { Sparkle } from 'lucide-react';
import Image from 'next/image';
import { PropsWithChildren, useState } from 'react';
import { useSupplierIntakeContext } from './context';

type SliderStepsProps = {
  steps: string[];
  currentStep: number;
  withDivider?: boolean;
};

export const SliderSteps = ({
  steps,
  currentStep,
  withDivider = false,
}: SliderStepsProps) => {
  const getStepsIcon = (idx: number) => {
    if (idx === currentStep) {
      return 'base';
    }

    if (idx > currentStep) {
      return 'disabled';
    }

    return 'completed';
  };

  return (
    <ul
      className={classNames(
        'flex items-center w-full text-xs font-medium sm:text-base py-5 px-9',
        { 'border-b border-gray-200': withDivider }
      )}
    >
      {steps.map((title, index) => (
        <li
          key={index}
          className={`hover:after:transition-all transition-all ease-out duration-1000 flex items-center justify-center w-full relative after:content-['']  after:w-full after:h-1  ${index >= currentStep ? 'after:bg-gray-200' : 'after:bg-primary-500'}  after:inline-block after:absolute after:top-5 after:left-[50%] [&:last-child]:after:w-0`}
        >
          <div
            className={classNames(
              'flex items-center justify-center flex-col gap-[12px] text-xs font-bold',
              {
                'text-text-primary': index <= currentStep,
                'text-gray-400 ': index > currentStep,
              }
            )}
          >
            <Image
              src={`/icons/step-icon-${getStepsIcon(index)}.svg`}
              className="mt-[9px] z-20"
              alt="step icon"
              width={24}
              height={24}
            />
            {title}
          </div>
        </li>
      ))}
    </ul>
  );
};

const Init = () => {
  const INTAKE_STEPS = [
    {
      name: 'Company Details',
      description: 'Share key information about your business',
    },
    {
      name: 'Basic Information',
      description: 'Tell us about your leadership and team',
    },
    {
      name: 'Certifications and Compliance',
      description: 'Share details about your industry and demographic certifications',
    },
    {
      name: 'Market & Industry',
      description: 'Define your industry focus and target markets',
    },
    {
      name: 'Customer Information',
      description: 'Highlight your experience and key clients',
    },
    {
      name: 'NAICS Codes',
      description: 'Classify your business for enterprise sourcing',
    },
  ];
  const { setCurrentStep } = useSupplierIntakeContext();
  const [nextStep, setNextStep] = useState(false);

  const IntakeSteps = () => {
    return (
      <div className="flex flex-col gap-4">
        {INTAKE_STEPS.map(({ name, description }) => (
          <div className="flex gap-2 items-center">
            <div className="rounded-full flex items-center justify-center bg-white w-[1.5rem] h-[1.5rem]">
              <IconCheck size={18} stroke={3} color="#1d2939" />
            </div>
            <p>
              <span className="text-text-primary font-bold">{name} - </span>{' '}
              <span className="text-text-secondary">{description}</span>
            </p>
          </div>
        ))}
      </div>
    );
  };

  const render = () => {
    if (nextStep) {
      return (
        <div className="flex flex-col gap-6">
          <p className="text-text-primary text-xl font-bold">
            Supplier Intake - What to Expect:
          </p>

          <IntakeSteps />

          <div>
            <Divider color="#83cadd" />
          </div>

          <div className="flex flex-col gap-2">
            <div className="flex gap-2 items-center">
              <Sparkle size={16} color="#e67a0f" />
              <p className="text-text-primary">
                <strong>Estimated Time to Complete:</strong> 20-30 min
              </p>
            </div>

            <div className="flex gap-2 items-center">
              <Sparkle size={16} color="#e67a0f" />
              <p className="text-text-primary">
                <strong>Progress is Saved Automatically</strong>
              </p>
            </div>
          </div>

          <div className="w-[140px] pt-8">
            <Button
              variant="secondary"
              onClick={() => setCurrentStep('details')}
              text="Begin"
              suffixIcon={<IconArrowRight />}
            />
          </div>
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-6">
        <p className="text-text-primary text-3xl font-bold">
          Welcome to Scaleup!
        </p>

        <p className="text-text-primary text-lg">
          Now that you're officially a Scaleup supplier, it's time for
          onboarding!
        </p>

        <div className="flex gap-4 items-start">
          <div className="pt-1">
            <Sparkle size={16} color="#e67a0f" />
          </div>
          <div className="flex flex-col gap-2">
            <p className="text-text-primary font-bold">
              Step 1: Supplier Intake
            </p>
            <p className="text-text-primary">
              This form helps us to build a robust company profile for you,
              ensuring you receive the right enterprise matches and
              opportunities.
            </p>
          </div>
        </div>

        <div className="w-[140px] pt-8">
          <Button
            variant="secondary"
            onClick={() => setNextStep(true)}
            text="Continue"
            suffixIcon={<IconArrowRight />}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="rounded-[2rem] bg-primary-100 flex justify-between gap-8 p-16">
      <div className="flex items-center p-8 max-w-[40rem]">{render()}</div>
      <div className="hidden lg:flex items-center">
        <Image src="/supplier-intake.png" alt="" width={460} height={552} />
      </div>
    </div>
  );
};

type IntakeBaseProps = {
  currentStep: number;
} & PropsWithChildren;

export const IntakeBase = ({ currentStep, children }: IntakeBaseProps) => {
  return (
    <div className="flex flex-col gap-4 bg-white w-full min-h-[90vh] rounded-[2rem] p-[2.5rem]">
      <div className="flex gap-2 items-center">
        <div className="rounded-xl w-[3rem] h-[3rem] bg-primary-50 flex items-center justify-center">
          <div className=" bg-primary-500 w-[1.5rem] h-[1.5rem]">
            <IconListCheck color="#d3ecf3" />
          </div>
        </div>

        <p className="text-text-primary font-bold text-3xl">Supplier Intake</p>
      </div>
      <SliderSteps
        steps={[
          'Company Details',
          'Certifications and Compliance',
          'Market & Industry',
          'Company Structure',
          'NAICS Codes',
          'Review',
        ]}
        currentStep={currentStep}
      />
      <div className="pt-4 w-full">{children}</div>
    </div>
  );
};

export const FormSection = ({
  icon,
  children,
  withDivider = false,
  leftPadding = false,
}: {
  icon: JSX.Element;
  withDivider?: boolean;
  leftPadding?: boolean;
} & PropsWithChildren) => {
  return (
    <div className={classNames('py-4 w-full', { 'pl-14': leftPadding })}>
      <div className="flex gap-4 pb-6 w-full">
        <div className="pt-1">{icon}</div>
        <div className="w-full">{children}</div>
      </div>
      {withDivider && <Divider color="#bfe4ed" />}
    </div>
  );
};
